/**
 * The DHA spec documents status codes (400 / 401 / 404 / 409) in prose and defines
 * no error response schema at all. So we normalise everything the network can throw
 * at us into one shape: `messageKey` is always an i18n key we control, with the
 * server's own text kept in `detail` for the console and telemetry.
 *
 * ONE server-authored string does reach the citizen, in `reason`. A generic "this
 * application has already been processed" told someone whose cancel was refused
 * nothing they could act on, while the gateway had already said exactly why —
 * "Application is in the incorrect state for the requested operation". `reason`
 * carries that sentence and only that sentence; see `extractReason` for how narrow
 * the accepted shape is, and why the machine-facing descriptions never qualify.
 *
 * See docs/api-gaps.md #3.
 */

export type ApiErrorKind =
  | 'network'
  | 'timeout'
  | 'aborted'
  | 'bad_request'
  | 'unauthorized'
  | 'forbidden'
  | 'not_found'
  | 'conflict'
  | 'rate_limited'
  | 'server'
  | 'parse'
  | 'unknown'
  /**
   * The server no longer recognises this browser's session, or the session does not
   * own the application it is asking about — a 401 that survived one re-bootstrap,
   * or a 403 `FORBIDDEN`. Distinct from `forbidden` because the response is
   * different: the wizard restarts (dev plan §3.2), rather than showing an error the
   * citizen could act on. Never retryable — the retry has already happened.
   */
  | 'session_expired'

export interface ApiErrorInit {
  kind: ApiErrorKind
  status?: number
  /** i18n key under the `errors` namespace. Never raw server prose. */
  messageKey: string
  /** Best-effort server text. Diagnostics only — never rendered to a citizen. */
  detail?: string
  /**
   * The gateway's own explanation, when it supplied one in prose a citizen can read.
   * Rendered INSTEAD of the `messageKey` catalogue string — it is always the more
   * specific of the two. Absent for every error the gateway did not explain.
   */
  reason?: string
  /** Echoed from the X-Correlation-Id we sent, so support can trace one request. */
  correlationId?: string
  /**
   * The VFS backend's machine-readable code from its envelope — `FORBIDDEN`,
   * `OFFICER_REQUIRED`, `CSRF_REJECTED`, `RATE_LIMITED`, `DHA_API_ERROR`, … — for
   * the callers that must branch on more than the status: two 403s with different
   * codes mean "restart the wizard" and "sign an officer in" respectively.
   */
  errorCode?: string
  /**
   * The parsed error body, kept whole.
   *
   * `detail` is a summary built for a human reading a console line; this is the
   * envelope it was summarised FROM, and it is what the api-log forwards so a
   * failure can be diagnosed after the fact from the trail alone. Diagnostics
   * only — like `detail`, never rendered to a citizen.
   */
  responseBody?: unknown
  retryable?: boolean
  cause?: unknown
}

const KIND_BY_STATUS: Record<number, ApiErrorKind> = {
  400: 'bad_request',
  401: 'unauthorized',
  403: 'forbidden',
  404: 'not_found',
  409: 'conflict',
  429: 'rate_limited',
}

/**
 * Retrying a 4xx just replays the same rejection, and none of the DHA write
 * endpoints are idempotent — a retried `initiate` or `finalise` creates a second
 * application. Only transport failures and 5xx are safe to repeat.
 */
const RETRYABLE_KINDS: ReadonlySet<ApiErrorKind> = new Set([
  'network',
  'timeout',
  'server',
  'rate_limited',
])

/**
 * A real Error subclass rather than a plain object: react-query, error boundaries
 * and every debugger in the chain expect a stack trace, and `instanceof Error`
 * checks in third-party code should not silently take the wrong branch.
 */
export class ApiError extends Error {
  override readonly name = 'ApiError'
  readonly kind: ApiErrorKind
  readonly status?: number
  readonly messageKey: string
  readonly detail?: string
  readonly reason?: string
  readonly correlationId?: string
  readonly errorCode?: string
  readonly responseBody?: unknown
  readonly retryable: boolean

  constructor(init: ApiErrorInit) {
    // The Error message is for developers; the citizen-facing text comes from
    // `messageKey` via the i18n catalogue.
    super(init.detail ?? init.messageKey, { cause: init.cause })
    this.kind = init.kind
    this.status = init.status
    this.messageKey = init.messageKey
    this.detail = init.detail
    this.reason = init.reason
    this.correlationId = init.correlationId
    this.errorCode = init.errorCode
    this.responseBody = init.responseBody
    this.retryable = init.retryable ?? RETRYABLE_KINDS.has(init.kind)
  }
}

export function isApiError(value: unknown): value is ApiError {
  return value instanceof ApiError
}

export function createApiError(init: ApiErrorInit): ApiError {
  return new ApiError(init)
}

export function kindForStatus(status: number): ApiErrorKind {
  return KIND_BY_STATUS[status] ?? (status >= 500 ? 'server' : 'unknown')
}

/**
 * Probes the shapes an error body is plausibly in. We have no contract, so this is
 * a best-effort dig through the conventions DHA's stack might use, ending at raw
 * text. Truncated because an HTML error page from a proxy is a valid outcome here.
 */
export function extractDetail(body: unknown): string | undefined {
  if (typeof body === 'string') {
    const text = body.trim()
    return text.length > 0 ? text.slice(0, 500) : undefined
  }

  if (typeof body !== 'object' || body === null) return undefined

  const record = body as Record<string, unknown>

  /**
   * The gateway's real error envelope, confirmed against the dev host (2026-08-20)
   * and documented nowhere:
   *
   *   {
   *     "response": {
   *       "code": 400,
   *       "messageType": "ERROR",
   *       "description": "HTTP 400: body [BODY_REMOVED] : {'idNo': ['Invalid value.']}",
   *       "error_reference_no": "289828c9-…",
   *       "request_guid": "1b1d0a3b…",
   *       "validation_failures": [["body [BODY_REMOVED]", {"idNo": ["Invalid value."]}]]
   *     },
   *     "result": false,
   *     "uniqueReference": "6e6ec7ac-…"
   *   }
   *
   * `validation_failures` is field-level, which is exactly what api-gaps.md #3 asked
   * for. It is preferred over `description` because the latter embeds a stringified
   * Python dict, and `error_reference_no` is appended because it is DHA's own handle
   * for the failure — the thing to quote in a support ticket.
   */
  const envelope = record.response
  if (typeof envelope === 'object' && envelope !== null) {
    const inner = envelope as Record<string, unknown>
    const fieldErrors = formatValidationFailures(inner.validation_failures)
    const description = typeof inner.description === 'string' ? inner.description : undefined
    const summary = fieldErrors ?? description

    if (summary) {
      const ref = inner.error_reference_no
      const suffix = typeof ref === 'string' && ref ? ` (DHA ref ${ref})` : ''
      return `${summary}${suffix}`.slice(0, 500)
    }
  }

  const candidates = [
    record.message,
    record.Message,
    record.error,
    record.Error,
    record.detail,
    record.Detail,
    record.title,
    record.Description,
    Array.isArray(record.errors)
      ? (record.errors[0] as Record<string, unknown> | undefined)?.message
      : undefined,
  ]

  for (const candidate of candidates) {
    if (typeof candidate === 'string' && candidate.trim().length > 0) {
      return candidate.trim().slice(0, 500)
    }
  }

  return undefined
}

/**
 * Pulls the one citizen-readable sentence out of the gateway's `description`.
 *
 * The gateway answers a refused cancel with:
 *
 *   "HTTP 409: Conflict (Application is in the incorrect state for the requested operation)"
 *
 * — an HTTP preamble wrapped around a plain sentence. The sentence is the answer the
 * citizen came for; the preamble is not. Everything before the bracket is discarded.
 *
 * The accepted shape is deliberately narrow, because the SAME field also carries
 * machine debris that must never reach a screen:
 *
 *   "HTTP 400: body [BODY_REMOVED] : {'idNo': ['Invalid value.']}\n (validation failure)"
 *
 * Requiring a bare HTTP reason phrase (letters and spaces) between the colon and the
 * bracket rejects that one outright — `body [BODY_REMOVED] : {…}` is not a reason
 * phrase — rather than leaving it to a blocklist of debris characters to catch. Those
 * guards stay anyway, as a second line: a field with no schema can hold anything, and
 * falling back to the catalogue string costs a citizen nothing.
 */
const GATEWAY_REASON = /^HTTP\s+\d{3}:\s*[A-Za-z ]*\(([^()]+)\)$/
const MACHINE_DEBRIS = /[{}[\]\\<>|]|BODY_REMOVED|_[a-z]+_/

/**
 * The one gate a server-authored string must pass before a citizen reads it.
 *
 * Extracted from `extractReason` so §4.8's `DHA_API_ERROR` message can reuse it. That
 * field is DHA's own error text, passed through verbatim by VFS's backend, and it can
 * carry the same machine debris the gateway's `description` does — so it gets the same
 * narrow treatment rather than a second, laxer one.
 */
export function citizenSafeProse(value: unknown): string | undefined {
  if (typeof value !== 'string') return undefined

  const prose = value.trim()
  if (!prose || prose.length > 200) return undefined
  if (MACHINE_DEBRIS.test(prose)) return undefined
  // Two words is a label ("validation failure"), not an explanation.
  if (prose.split(/\s+/).length < 3) return undefined

  // Servers write these without terminal punctuation; the UI renders them as a
  // sentence beside our own, which all end in a full stop.
  return /[.!?]$/.test(prose) ? prose : `${prose}.`
}

export function extractReason(body: unknown): string | undefined {
  if (typeof body !== 'object' || body === null) return undefined

  const envelope = (body as Record<string, unknown>).response
  if (typeof envelope !== 'object' || envelope === null) return undefined

  const description = (envelope as Record<string, unknown>).description
  if (typeof description !== 'string') return undefined

  return citizenSafeProse(GATEWAY_REASON.exec(description.trim())?.[1])
}

/** Maps a normalised kind onto the i18n key the UI renders. */
export function messageKeyForKind(kind: ApiErrorKind): string {
  return `errors:api.${kind}`
}

/** Converts a thrown transport-level value into an ApiError. */
export function normalizeThrown(cause: unknown, correlationId?: string): ApiError {
  if (isApiError(cause)) return cause

  if (cause instanceof DOMException && cause.name === 'AbortError') {
    return new ApiError({
      kind: 'aborted',
      messageKey: messageKeyForKind('aborted'),
      correlationId,
      cause,
    })
  }

  if (cause instanceof DOMException && cause.name === 'TimeoutError') {
    return new ApiError({
      kind: 'timeout',
      messageKey: messageKeyForKind('timeout'),
      correlationId,
      cause,
    })
  }

  return new ApiError({
    kind: 'network',
    messageKey: messageKeyForKind('network'),
    detail: cause instanceof Error ? cause.message : undefined,
    correlationId,
    cause,
  })
}

/**
 * Flattens the gateway's `validation_failures` into one readable line.
 *
 * Shape is a list of `[location, detail]` pairs where `detail` is either a string
 * or a map of field name -> list of messages:
 *
 *   [["body [BODY_REMOVED]", {"idNo": ["Invalid value."]}],
 *    ["X-REQUEST-UUID", "Missing parameter in [header]"]]
 *
 * -> "idNo: Invalid value.; X-REQUEST-UUID: Missing parameter in [header]"
 */
function formatValidationFailures(value: unknown): string | undefined {
  if (!Array.isArray(value)) return undefined

  const parts: string[] = []

  for (const failure of value) {
    if (!Array.isArray(failure) || failure.length < 2) continue
    const [location, detail] = failure as [unknown, unknown]

    if (typeof detail === 'string') {
      const where = typeof location === 'string' ? location : 'request'
      // The body placeholder is noise to a reader; a header name is not.
      parts.push(where.startsWith('body') ? detail : `${where}: ${detail}`)
      continue
    }

    if (typeof detail === 'object' && detail !== null) {
      for (const [field, messages] of Object.entries(detail as Record<string, unknown>)) {
        const text = Array.isArray(messages) ? messages.join(' ') : String(messages)
        parts.push(`${field}: ${text}`)
      }
    }
  }

  return parts.length > 0 ? parts.join('; ') : undefined
}
